import { Component, OnInit, PLATFORM_ID, inject, OnDestroy } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

import { ProductCardComponent } from '../../shared/components/product-card/product-card.component';
import { CarouselComponent } from '../../shared/components/carousel/carousel.component';
import { TabsComponent, TabItem } from '../../shared/components/tabs/tabs.component';
import { ListItemComponent } from '../../shared/components/list-item/list-item.component';
import { CollapsePanelComponent } from '../../shared/components/collapse-panel/collapse-panel.component';
import { ProductV2Service } from '../../core/services/product-v2.service';
import {
  ProductV2,
  ProductV2Variation,
} from '../../core/models/product-v2.model';
import { ProductService } from '../../core/services/product.service';
import { ProductCategory } from '../../core/models/product.model';
import { FaqService } from '../../core/services/faq.service';
import { FaqCategory } from '../../core/models/faq.model';
import { ListItem } from '../../shared/components/list-item/list-item.interface';
import { CartFacade } from '../../core/store/cart/cart.facade';
import { AvailableOffersFacade } from '../../core/store/available-offers/available-offers.facade';
import { ToastService } from '../../shared/services/toast.service';
import { CartService } from '../../shared/services/cart.service';
import { GtmService, Ga4Item } from '../../core/services/gtm.service';
import { NavigationFacade } from '../../core/store/navigation/navigation.facade';
import { sanitizeSlug } from '../../core/utils/utils';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { UtmPreservationService } from '../../core/services/utm-preservation.service';
import { I18nService } from '../../core/services/i18n.service';
import { CategoryHelperService } from '../../shared/services/category-helper.service';
import { filterProductsForCardDisplay } from '../../core/utils/product-card-display.util';
import { BlipGoContractService } from '../../core/services/blip-go-contract.service';

@Component({
  selector: 'umkt-not-found',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ProductCardComponent,
    CarouselComponent,
    TabsComponent,
    ListItemComponent,
    CollapsePanelComponent,
    I18nPipe
  ],
  templateUrl: './not-found.component.html',
  styleUrls: ['./not-found.component.scss'],
})
export class NotFoundComponent implements OnInit, OnDestroy {
  isBrowser: boolean = false;
  products: ProductV2[] = [];
  categories: ProductCategory[] = [];
  tabs: TabItem[] = [];
  activeTabIdSubject = new BehaviorSubject<string>('');
  faqCategories: FaqCategory[] = [];
  faqItems: ListItem[] = [];
  selectedFaqItem: string = '';
  cartFacade = inject(CartFacade);
  navigationFacade = inject(NavigationFacade);
  availableOffersFacade = inject(AvailableOffersFacade);
  private i18nService = inject(I18nService);
  private blipGoContractService = inject(BlipGoContractService);
  private destroy$ = new Subject<void>();
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly productV2Service = inject(ProductV2Service);
  private readonly productService = inject(ProductService);
  private readonly faqService = inject(FaqService);
  private readonly toastService = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  private cartService = inject(CartService);
  public gtm = inject(GtmService);
  private utmPreservationService = inject(UtmPreservationService);
  private categoryHelper = inject(CategoryHelperService);

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
    this.i18nService.getCurrentLanguage()
    .pipe(takeUntil(this.destroy$))
    .subscribe(() => {

      this.productService.clearCategoriesCache();

      const currentCategory = this.activeTabIdSubject.value;

      this.getCategories();

      if (currentCategory) {
        setTimeout(() => {
          this.activeTabIdSubject.next(currentCategory);
        });
      }
    });
    this.getCategories();
    this.getFaqCategories();
    this.activeTabIdSubject.subscribe(tabId => {
      if (tabId && tabId !== '') {
        this.getProductsByCategory();
      }
    });
  }

  getProductsByCategory(): void {
    const categoryName = this.activeTabIdSubject.value;
    if (categoryName) {
      this.productV2Service
        .getProductsByCategory(categoryName)
        .subscribe({
          next: resp => {
            this.products = filterProductsForCardDisplay(resp.data, {
              hideBlipGo: this.blipGoContractService.shouldHideBlipGo(),
            });
            if (this.products && this.products.length > 0) {
              this.availableOffersFacade.mapProductsVariationsToOffers(this.products);
            }
            const items: Ga4Item[] = (this.products || []).map((p, idx) => ({
              item_id: p.sku || p.uuid || p.id,
              item_name: p.name,
              item_category: categoryName,
              index: idx + 1,
              price: p.salesPrice ?? p.price ?? 0
            }));
            this.gtm.viewItemList('404-page', categoryName, items);
          },
          error: error => {
            console.error('Error fetching products for 404 page:', error);
          },
        });
    }
  }

  getCategories(): void {
    this.productService.getCategories().subscribe({
      next: resp => {

        const filteredCategories =
          this.categoryHelper.filterAndSortCategories(resp.data);

        this.categories = filteredCategories;

        this.activeTabIdSubject.next(this.categories[0]?.name);

        this.tabs = this.categories.map(category => ({
          id: category.name,
          label: category.name,
          disabled: false,
        }));
      },
      error: error => {
        console.error('Error fetching categories for 404 page:', error);
      },
    });
  }

  onCategoryChange(category: string) {
    this.activeTabIdSubject.next(category);
  }

  getCurrentCategory(): any {
    return this.categories.find(
      tab => tab.name === this.activeTabIdSubject.value
    );
  }

  onLearnMore(product: ProductV2) {
    const categorySlug = sanitizeSlug(this.getCurrentCategory()?.name || '');
    const productSlug = sanitizeSlug(product.name);
    const categoryName = this.getCurrentCategory()?.name || '';    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
    this.navigationFacade.setCurrentProduct(product.uuid, categoryName);

    const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({
      ...(this.route.snapshot?.queryParams || {}),
    });

    this.router.navigate([categorySlug, productSlug], { queryParams });
  }

  onAddToCartFromCard(
    event: { plan?: ProductV2Variation | null; quantity: number },
    product: ProductV2
  ) {
    this.cartFacade.addToCart(product, event.quantity, event.plan || undefined);
  }

  goHome() {
    const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({
      ...(this.route.snapshot?.queryParams || {}),
    });

    this.router.navigate(['/'], { queryParams });
  }

  getFaqCategories(): void {
    this.faqService.getFaqsByCategory().subscribe({
      next: resp => {
        this.faqCategories = resp.categories || [];
        this.faqItems = this.faqCategories.map(category => ({
          id: category.uuid,
          text: category.name,
          rightIcon: 'mdn-Icon-arrow-right mdn-Icon--md',
          disabled: false,
          questions:
            category.qa?.map(faq => ({
              id: faq.uuid,
              title: faq.question,
              content: faq.answer,
            })) || [],
          onClick: () => this.onFaqItemSelected(category.uuid),
        }));

        if (this.faqItems.length > 0) {
          this.selectedFaqItem = this.faqItems[0].id;
        } else {
          this.selectedFaqItem = '';
        }
      },
      error: error => {
        console.error('Erro ao buscar categorias de FAQ:', error);
        this.faqItems = [];
        this.selectedFaqItem = '';
      },
    });
  }

  onFaqItemSelected(itemId: string): void {
    this.selectedFaqItem = itemId;
  }

  onFaqSelectChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.selectedFaqItem = select.value || '';
  }

  getSelectedFaqItem(): any | null {
    if (!this.selectedFaqItem || this.selectedFaqItem === '') return null;
    return this.faqItems.find(item => item.id === this.selectedFaqItem) || null;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
